import { useCallback, useEffect, useState } from "react";
import { DatabaseBackup, Download, ChevronDown, AlertTriangle, CheckCircle2, Cloud, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { http } from "@/lib/api";

// Lote 124 — cartão "Backup" da Administração: status do último backup (o do Mac, que salva na pasta do iCloud Drive,
// ou o baixado à mão) e o botão "Baixar backup agora". O arquivo é criptografado no servidor; sem a frase-senha não abre.
const fmt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
};
const mb = (b) => (b == null ? "" : b >= 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const ORIGEM = { mac: "pelo Mac (iCloud Drive)", botao: "baixado à mão", nuvem: "enviado à nuvem" };

const ALERTA = {
  nao_configurado: { cls: "border-amber-200 bg-amber-50 text-amber-900", titulo: "Backup ainda não configurado", txt: "O servidor não tem a chave do backup. Rode o instalador no Mac (veja “Como funciona e como restaurar”, abaixo)." },
  nunca: { cls: "border-amber-200 bg-amber-50 text-amber-900", titulo: "Nenhum backup confirmado ainda", txt: "O backup está configurado, mas nenhuma cópia chegou ao Mac. Use \"Baixar backup agora\" ou rode o script no Mac." },
  atrasado: { cls: "border-red-200 bg-red-50 text-red-900", titulo: "Backup atrasado", txt: "Faz mais de 48 horas que nenhuma cópia foi confirmada. Confira se o Mac esteve ligado e baixe uma cópia agora." },
};

// Lote 135 — linha "Cópia na nuvem" (Cloudflare R2): o servidor envia uma cópia cifrada todo dia às 7h20.
const NUVEM_ALERTA = {
  nao_configurada: { cls: "text-stone-500", txt: "ainda não configurada" },
  nunca: { cls: "text-amber-700", txt: "ainda sem nenhum envio" },
  atrasada: { cls: "text-red-700", txt: "atrasada (mais de 48 horas sem enviar)" },
};

export default function BackupCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyNuvem, setBusyNuvem] = useState(false);
  const [ajuda, setAjuda] = useState(false);
  const load = useCallback(() => http.get("/admin/backup/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const baixar = async () => {
    setBusy(true);
    try {
      const r = await http.get("/admin/backup/baixar", { responseType: "blob", timeout: 300000 });
      const cd = r.headers?.["content-disposition"] || "";
      const nome = (/filename="?([^";]+)"?/.exec(cd) || [])[1] || "iapauta-backup.iabak";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Backup baixado", { description: "Guarde o arquivo na pasta IApauta-Backup do iCloud Drive." });
      load();
    } catch (e) {
      let msg = "Não consegui gerar o backup";
      try { const t = await e?.response?.data?.text?.(); const j = t && JSON.parse(t); if (j?.detail) msg = j.detail; } catch { /* ignora */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const enviarNuvem = async () => {
    setBusyNuvem(true);
    try {
      const r = await http.post("/admin/backup/nuvem/agora", {}, { timeout: 300000 });
      toast.success("Cópia enviada para a nuvem", { description: `${mb(r.data?.bytes)} · ${r.data?.restam ?? "?"} arquivo(s) guardado(s)` });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui enviar a cópia para a nuvem");
      load();
    } finally { setBusyNuvem(false); }
  };

  if (!st) return null;
  const nv = st.nuvem || {};
  const nvAl = nv.alerta ? NUVEM_ALERTA[nv.alerta] : null;
  const al = st.alerta ? ALERTA[st.alerta] : null;
  const ok = fmt(st.ultimo_ok_at);
  return (
    <section data-testid="admin-backup" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2">
            <DatabaseBackup className="w-4 h-4 text-stone-500" strokeWidth={1.75} /> Backup
            {!st.alerta && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="backup-em-dia"><CheckCircle2 className="w-3.5 h-3.5" /> em dia</span>}
          </h2>
          <p className="text-sm text-stone-600 mt-1" data-testid="backup-ultimo">
            {ok
              ? <>Último backup confirmado: <b className="text-stone-900">{ok}</b> · {ORIGEM[st.ultimo_ok_origem] || st.ultimo_ok_origem}{st.ultimo_bytes ? ` · ${mb(st.ultimo_bytes)}` : ""}{st.ultimo_docs ? ` · ${st.ultimo_docs} registros` : ""}</>
              : "Nenhum backup confirmado ainda."}
          </p>
          <p className="text-sm text-stone-600 mt-0.5 flex items-start gap-1.5" data-testid="backup-nuvem" data-alerta={nv.alerta || "ok"}>
            <Cloud className="w-3.5 h-3.5 mt-1 shrink-0 text-stone-500" strokeWidth={1.75} />
            <span>
              Cópia na nuvem:{" "}
              {nvAl
                ? <span className={`font-semibold ${nvAl.cls}`}>{nvAl.txt}</span>
                : <><b className="text-stone-900">ok em {fmt(nv.ok_at)}</b>{nv.bytes ? ` · ${mb(nv.bytes)}` : ""}{nv.arquivos ? ` · ${nv.arquivos} arquivos guardados` : ""}</>}
              {nv.erro && nv.configurada && <span className="block text-xs text-red-700" data-testid="backup-nuvem-erro">Último erro: {nv.erro}</span>}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
        {nv.configurada && (
          <Button data-testid="backup-nuvem-enviar" variant="outline" onClick={enviarNuvem} disabled={busyNuvem || busy || !(st.configurado?.senha)} className="gap-2">
            <CloudUpload className="w-4 h-4" strokeWidth={2} /> {busyNuvem ? "Enviando…" : "Enviar para a nuvem agora"}
          </Button>
        )}
        <Button data-testid="backup-baixar" onClick={baixar} disabled={busy || !(st.configurado?.senha)} className="gap-2 bg-stone-900 text-white hover:bg-stone-800">
          <Download className="w-4 h-4" strokeWidth={2} /> {busy ? "Gerando…" : "Baixar backup agora"}
        </Button>
        </div>
      </div>

      {al && (
        <div role="alert" data-testid="backup-alerta" data-alerta={st.alerta} className={`mt-3 flex gap-2.5 rounded-md border px-3.5 py-2.5 text-sm ${al.cls}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
          <div><p className="font-semibold">{al.titulo}</p><p className="mt-0.5">{al.txt}</p></div>
        </div>
      )}

      <button type="button" data-testid="backup-ajuda-toggle" onClick={() => setAjuda((v) => !v)} aria-expanded={ajuda}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900">
        Como funciona e como restaurar <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ajuda ? "rotate-180" : ""}`} />
      </button>
      {ajuda && (
        <div data-testid="backup-ajuda" className="mt-2 text-sm text-stone-700 space-y-2 max-w-3xl">
          <p>Todo dia às 7h10 o seu Mac pede o backup ao servidor e salva em <b>iCloud Drive &gt; IApauta-Backup</b> (e numa cópia local). O arquivo é criptografado: sem a <b>frase-senha</b> (guardada no seu gerenciador de senhas) ele não abre. Ficam os últimos 7 dias e um por semana até 35 dias.</p>
          <p>Além do Mac, o servidor envia sozinho todo dia às 7h20 uma segunda cópia (já criptografada) para um cofre na nuvem (Cloudflare R2), com a mesma rotação. Se ficar 2 dias sem enviar, um e-mail avisa. O botão <b>Enviar para a nuvem agora</b> faz isso na hora.</p>
          <p>Se o Mac ficar 2 dias sem receber, este cartão e um e-mail avisam. O botão acima gera uma cópia na hora (pelo celular também).</p>
          <p>Instalar (uma vez): no Terminal do Mac, dentro da pasta <b>ferramentas/backup</b> do projeto, rode <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">bash instalar-backup-mac.sh</code>. Ele cria a frase-senha (mostrada só na sua tela), grava no servidor e agenda o backup diário.</p>
          <p>Restaurar: roteiro no arquivo <b>ferramentas/backup/LEIA-ME.md</b> do projeto. Para conferir um arquivo sem restaurar: <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">python3 restaurar_backup.py ARQUIVO.iabak --verificar</code>.</p>
          {(st.historico || []).length > 0 && (
            <ul className="text-xs text-stone-500 space-y-0.5 pt-1" data-testid="backup-historico">
              {st.historico.map((h) => (
                <li key={h.backup_id}>{fmt(h.quando)} · {ORIGEM[h.origem] || h.origem} · {mb(h.bytes)} · {h.confirmado ? "confirmado" : "sem confirmação"}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
